/** Space kept between the word and the edges of the text in view */
const margin = 16;
/** Kept in view above the sheet: the word looked up, in its context */
const linesInView = 3;

/**
 * How far to move the text up so the word stays in view above the bottom
 * sheet: unchanged while it's already in view, otherwise centring it in the
 * space left. Coordinates are as on screen, with the text already moved up by
 * `currentShift`.
 */
const shiftToReveal = ({
  word,
  visibleTop,
  visibleBottom,
  currentShift,
}: {
  word: { top: number; bottom: number };
  visibleTop: number;
  visibleBottom: number;
  currentShift: number;
}) => {
  if (
    word.top >= visibleTop + margin &&
    word.bottom <= visibleBottom - margin
  ) {
    return currentShift;
  }
  const wordCentre = (word.top + word.bottom) / 2;
  const visibleCentre = (visibleTop + visibleBottom) / 2;
  return Math.max(0, Math.round(currentShift + wordCentre - visibleCentre));
};

const bottomOf = (selector: string) => {
  const rect = document.querySelector(selector)?.getBoundingClientRect();
  return rect && rect.height > 0 ? rect.bottom : 0;
};

const pageRect = () =>
  document.querySelector(".outerContainer")?.getBoundingClientRect();

/** Below the title bar, and any video over the text */
const textTop = (page: DOMRect) =>
  Math.max(page.top, bottomOf("#titleBar"), bottomOf("#mediaPanel"));

/** The sheet's height, leaving a few lines of text in view above it */
export const bottomSheetHeight = () => {
  const page = pageRect();
  const bookContent = document.querySelector("#bookContent");
  if (!page || !bookContent) {
    return undefined;
  }
  const lineHeight = parseFloat(getComputedStyle(bookContent).lineHeight);
  return Math.round(
    page.bottom - textTop(page) - linesInView * lineHeight - 2 * margin
  );
};

/**
 * The text's shift (see `shiftToReveal`) showing the word's element above a
 * sheet `sheetHeight` high, or `currentShift` while the word isn't on the page
 */
export const shiftToRevealWord = ({
  wordElement,
  currentShift,
  sheetHeight,
}: {
  wordElement: Element | undefined;
  currentShift: number;
  sheetHeight: number;
}) => {
  const word = wordElement?.getBoundingClientRect();
  const page = pageRect();
  if (!word || !page) {
    return currentShift;
  }
  return shiftToReveal({
    word,
    visibleTop: textTop(page),
    visibleBottom: page.bottom - sheetHeight,
    currentShift,
  });
};

export const _functionsForUnitTests = { shiftToReveal };
